'use client';

import Image from 'next/image';
import Link from 'next/link';

import {
  Badge,
  BottomFixed,
  Button,
  CountdownTimer,
  Icon,
  PromotionPacks,
  QuantitySelector,
} from '@/components';
import { MustangArrow } from '@/components/ui/MustangArrow';
import { cn } from '@/libs';
import type { HighlightedRaffleDetails } from '@/types/models';
import {
  formatCurrency,
  getRaffleCtaLabel,
  isRaffleOnSale,
  isRaffleUpcoming,
  isWithinDays,
  type PromotionOffer,
  UPCOMING_SALE_BADGE_TEXT,
} from '@/utils';

type HighlightedRaffleLayoutProps = {
  raffle: HighlightedRaffleDetails;
  packOffers: PromotionOffer[];
  ticketQuantity: number;
  totalValue: number;
  addQuantity: (value: number) => void;
  handleComboClick: (pack: { quantity: number; percentage: number }) => void;
  handleChangeQuantity: (value: number) => void;
  goToCheckout: () => void;
  hasQuantityError: boolean;
};

export const HighlightedRaffleLayout = ({
  raffle,
  packOffers,
  ticketQuantity,
  totalValue,
  addQuantity,
  handleComboClick,
  handleChangeQuantity,
  goToCheckout,
  hasQuantityError,
}: HighlightedRaffleLayoutProps) => {
  const canBuy = isRaffleOnSale(raffle?.status);
  const isUpcoming = isRaffleUpcoming(raffle?.status);

  return (
    <div className="grid lg:grid-cols-2 gap-4 md:gap-2 lg:p-4 lg:gap-2 lg:rounded-lg lg:bg-background-variant">
      <div className="flex flex-col bg-background-variant rounded-lg relative">
        <Link href={`/detalhes/${raffle?.id}`}>
          <Image
            src={raffle?.urlBanner || ''}
            alt="banner principal"
            width={1080}
            priority
            height={400}
            unoptimized
            className="object-cover rounded-lg md:w-full md:h-[400px] lg:h-auto"
          />
        </Link>
        {isUpcoming && (
          <Badge
            data-cy="highlighted-raffle-upcoming-badge"
            icon="Rocket"
            text={UPCOMING_SALE_BADGE_TEXT}
            className="absolute top-3 left-3 w-auto gap-1 px-2 py-1 bg-secondary shadow-xl"
            cnText="text-secondary-foreground"
            iconProps={{ width: 14, height: 14 }}
          />
        )}
      </div>

      <div className="flex flex-col gap-4 lg:justify-between">
        <div className="rounded-lg space-y-4">
          <Link href={`/detalhes/${raffle?.id}`}>
            <div className="flex flex-row items-center justify-between w-full rounded-lg bg-background-variant lg:bg-background p-3">
              <div className="flex flex-col items-start">
                <span className="uppercase font-semibold md:text-lg">{raffle?.name}</span>
              </div>
              {raffle?.ticketPrice && (
                <div className="flex flex-col justify-start">
                  <span className="text-foreground/90 text-right text-xs">
                    adquira por
                  </span>
                  <span className="uppercase font-semibold text-xl">
                    {formatCurrency(raffle?.ticketPrice)}
                  </span>
                </div>
              )}
            </div>
          </Link>

          {isWithinDays(new Date(raffle?.drawDate || new Date()), 2) && (
            <div className="flex flex-row items-center justify-center w-full rounded-lg bg-background-variant lg:bg-background p-2.5">
              <div className="flex flex-col items-center justify-start">
                <span className="uppercase text-center font-semibold text-sm">
                  próximo sorteio
                </span>
                <CountdownTimer targetDate={new Date(raffle?.drawDate || new Date())} />
              </div>
            </div>
          )}
        </div>

        <div className={cn('lg:hidden', !canBuy && !isUpcoming && 'hidden')}>
          <BottomFixed className="p-3 !gap-0.5 rounded-b-none w-full max-w-screen-xl mx-auto md:p-6 md:inset-x-0 md:bottom-0 shadow-up-md">
            {!isUpcoming && (
              <>
                {packOffers.length > 0 && (
                  <PromotionPacks
                    offers={packOffers}
                    ticketPrice={raffle.ticketPrice || 0}
                    onSelectPack={handleComboClick}
                  />
                )}
                <BottomFixed.Header className="text-foreground flex flex-row justify-between items-start gap-1 md:gap-2 w-full">
                  {raffle?.addTicketQuantityButtonValue &&
                    raffle.addTicketQuantityButtonValue.some((value) => value > 0) &&
                    raffle.addTicketQuantityButtonValue.map((value: number) => (
                      <div className="flex-1 relative" key={value}>
                        {raffle.addTicketQuantityButtonValue &&
                          raffle.addTicketQuantityButtonValue[2] === value && (
                            <Badge
                              icon="Fire"
                              className="w-fit p-0.5 absolute -top-2 left-1/2 -translate-x-1/2 shadow-xl"
                              text="popular"
                              cnText="text-xxs font-medium"
                              iconProps={{ width: 12, height: 12 }}
                            />
                          )}
                        <Button
                          variant="secondary"
                          onClick={() => addQuantity(value)}
                          className="w-full h-12 text-xl rounded-sm border-2 border-foreground/10 bg-foreground/10 px-3 py-3.5"
                        >
                          {`+${value}`}
                        </Button>
                      </div>
                    ))}
                </BottomFixed.Header>

                <BottomFixed.Body className="flex flex-col rounded-lg gap-2 overflow-visible">
                  <div className="flex flex-row items-center justify-between w-full">
                    <div className="flex flex-1 flex-col items-start gap-0 md:gap-1">
                      <span className="text-foreground text-sm uppercase font-medium">
                        total
                      </span>
                      <span className="text-secondary uppercase font-semibold text-2xl md:text-2xl leading-none">
                        {formatCurrency(totalValue)}
                      </span>
                    </div>

                    <div className="relative z-10 pointer-events-auto">
                      <QuantitySelector
                        count={ticketQuantity}
                        onSetCount={handleChangeQuantity}
                        min={raffle?.minTicketsPerPurchase}
                        max={raffle?.maxTicketsPerPurchase}
                        incrementStep={raffle?.addTicketButtonStepIncrement}
                        decrementStep={raffle?.addTicketButtonStepDecrement}
                      />
                    </div>
                  </div>
                </BottomFixed.Body>
              </>
            )}

            <BottomFixed.Footer className="flex items-center justify-center text-foreground flex-col gap-2">
              <Button
                data-cy="highlighted-raffle-buy-button"
                variant="tertiary"
                onClick={goToCheckout}
                className={cn(
                  'h-12 w-full p-4 flex items-center space-x-2.5 text-base',
                  hasQuantityError && '!opacity-50',
                )}
                disabled={hasQuantityError || !canBuy}
              >
                <Icon.Dollar width={24} height={24} />
                <span>{getRaffleCtaLabel(raffle?.status, 'Compra Agora')}</span>
                <MustangArrow />
              </Button>
            </BottomFixed.Footer>
          </BottomFixed>
        </div>

        <div className={cn(canBuy || isUpcoming ? 'hidden lg:block' : 'hidden')}>
          <BottomFixed hasFooter>
            {!isUpcoming && (
              <>
                {packOffers.length > 0 && (
                  <div className="mb-4">
                    <PromotionPacks
                      offers={packOffers}
                      ticketPrice={raffle.ticketPrice || 0}
                      onSelectPack={handleComboClick}
                    />
                  </div>
                )}
                <BottomFixed.Header className="text-foreground flex flex-row justify-between items-start gap-2.5 w-full">
                  {raffle?.addTicketQuantityButtonValue &&
                    raffle.addTicketQuantityButtonValue.some((value) => value > 0) &&
                    raffle.addTicketQuantityButtonValue.map((value: number) => (
                      <div className="flex-1 relative" key={value}>
                        {raffle?.addTicketQuantityButtonValue &&
                          raffle.addTicketQuantityButtonValue[2] === value && (
                            <Badge
                              icon="Fire"
                              className="w-fit p-0.5 absolute -top-2 left-1/2 -translate-x-1/2 shadow-xl z-10"
                              text="popular"
                              cnText="text-xxs font-medium"
                              iconProps={{ width: 12, height: 12 }}
                            />
                          )}
                        <Button
                          variant="secondary"
                          onClick={() => addQuantity(value)}
                          className="w-full h-12 text-xl rounded-sm border-2 border-foreground/10 bg-foreground/10 px-6 py-3.5"
                        >
                          {`+${value}`}
                        </Button>
                      </div>
                    ))}
                </BottomFixed.Header>

                <BottomFixed.Body className="flex flex-col rounded-lg gap-2.5 overflow-visible">
                  <div className="flex flex-row items-center justify-between w-full">
                    <div className="flex flex-col items-start justify-start">
                      <span className="text-foreground text-xs uppercase">total</span>
                      <span className="text-secondary uppercase font-semibold text-xl">
                        {formatCurrency(totalValue)}
                      </span>
                    </div>

                    <div className="relative z-10 pointer-events-auto">
                      <QuantitySelector
                        count={ticketQuantity}
                        onSetCount={handleChangeQuantity}
                        min={raffle?.minTicketsPerPurchase}
                        max={raffle?.maxTicketsPerPurchase}
                        incrementStep={raffle?.addTicketButtonStepIncrement}
                        decrementStep={raffle?.addTicketButtonStepDecrement}
                      />
                    </div>
                  </div>
                </BottomFixed.Body>
              </>
            )}

            <BottomFixed.Footer className="flex items-center justify-center text-foreground flex-col gap-2">
              <Button
                data-cy="highlighted-raffle-checkout-button"
                variant="tertiary"
                onClick={goToCheckout}
                className={cn(
                  'h-12 w-full p-4 flex items-center space-x-2.5 text-base',
                  hasQuantityError && '!opacity-50',
                )}
                disabled={hasQuantityError || !canBuy}
              >
                <Icon.Dollar width={24} height={24} />
                <span>{getRaffleCtaLabel(raffle?.status, 'ir para pagamento')}</span>
              </Button>
            </BottomFixed.Footer>
          </BottomFixed>
        </div>
      </div>
    </div>
  );
};
